<script setup lang="ts">
import { ElTree } from "element-plus";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import type { TreeDefinitionDto, TreeNodeDto } from "~/api";
import { buildTreeNodeMap } from "~/composables/useGeoTree";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const props = defineProps<{
  treeDefinitionGuid: string;
  modelValue: string[];
  multiple?: boolean;
  showCheckbox?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Start the tree from a specific node's children instead of the root (by GUID) */
  rootNodeGuid?: string;
  /** Start the tree from a specific node's children instead of the root (by name) */
  rootNodeName?: string;
  /** Show level name tag next to each node (default: true) */
  showLevelName?: boolean;
  /** When true, show selected path as summary and expand tree on click (default: false) */
  collapsible?: boolean;
  /** Optional map of node GUID → count to display next to each node label */
  nodeCounts?: Map<string, number>;
  /** When true and nodeCounts is provided, disable checkboxes for nodes with a count of 0 */
  disableZeroCounts?: boolean;
  /** Expand first-level nodes on mount (default: true) */
  defaultExpanded?: boolean;
}>();

const treeRef = ref<InstanceType<typeof ElTree>>();
const editing = ref(false);

const emit = defineEmits<{
  (e: "update:modelValue", value: string[]): void;
  (
    e: "nodeSelected",
    payload: { node: TreeNodeDto; ancestors: TreeNodeDto[] },
  ): void;
}>();

const loading = ref(true);
const treeDefinition = ref<TreeDefinitionDto | null>(null);

// Flat map for quick lookups
const flatNodeMap = ref(new Map<string, TreeNodeDto>());

const loadTree = async () => {
  if (!props.treeDefinitionGuid) return;
  loading.value = true;
  try {
    treeDefinition.value = await apiClient.tree.getTreeDefinition(
      props.treeDefinitionGuid,
    );
    flatNodeMap.value.clear();
    buildTreeNodeMap(treeDefinition.value?.nodes || [], flatNodeMap.value);
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
};

onMounted(loadTree);

watch(() => props.treeDefinitionGuid, loadTree);

// Sync el-tree checked state when modelValue changes externally
watch(
  () => props.modelValue,
  (keys) => {
    treeRef.value?.setCheckedKeys(keys);
  },
);

// el-tree data format
interface ElTreeNode {
  id: string;
  label: string;
  depth: number;
  levelName: string;
  disabled: boolean;
  children: ElTreeNode[];
}

const mapNodes = (nodes: TreeNodeDto[]): ElTreeNode[] => {
  return nodes.map((node) => ({
    id: node.guid,
    label: node.name,
    depth: node.depth,
    levelName: node.levelName,
    disabled:
      props.disabled ||
      (props.disableZeroCounts &&
        !!props.nodeCounts &&
        (props.nodeCounts.get(node.guid) ?? 0) === 0),
    children: mapNodes(node.children),
  }));
};

const treeProps = {
  label: "label",
  children: "children",
  disabled: "disabled",
};

const findNodeByGuid = (
  nodes: TreeNodeDto[],
  guid: string,
): TreeNodeDto | undefined => {
  for (const node of nodes) {
    if (node.guid === guid) return node;
    const found = findNodeByGuid(node.children, guid);
    if (found) return found;
  }
  return undefined;
};

const findNodeByName = (
  nodes: TreeNodeDto[],
  name: string,
): TreeNodeDto | undefined => {
  for (const node of nodes) {
    if (node.name === name) return node;
    const found = findNodeByName(node.children, name);
    if (found) return found;
  }
  return undefined;
};

const effectiveRootNodes = computed(() => {
  if (!treeDefinition.value) return [];
  const allNodes = treeDefinition.value.nodes;
  if (props.rootNodeGuid) {
    const target = findNodeByGuid(allNodes, props.rootNodeGuid);
    return target ? target.children : allNodes;
  }
  if (props.rootNodeName) {
    const target = findNodeByName(allNodes, props.rootNodeName);
    return target ? target.children : allNodes;
  }
  return allNodes;
});

const treeData = computed(() => mapNodes(effectiveRootNodes.value));

const defaultExpandedKeys = computed(() => {
  if (props.defaultExpanded === false) return [];
  return effectiveRootNodes.value.map((n) => n.guid);
});

watch(editing, async (isEditing) => {
  if (!isEditing || !props.collapsible) return;
  await nextTick();
  const tree = treeRef.value;
  if (!tree?.store) return;
  const selectedIds = new Set(props.modelValue);
  const ancestorIds = new Set<string>();
  for (const guid of selectedIds) {
    for (const a of getAncestors(guid)) ancestorIds.add(a.guid);
  }
  for (const [key, node] of Object.entries(
    tree.store.nodesMap as Record<
      string,
      { expanded: boolean; isLeaf: boolean }
    >,
  )) {
    if (!node.isLeaf && (selectedIds.has(key) || ancestorIds.has(key)))
      node.expanded = true;
  }
});

// ---- Selection Propagation ----
const getAncestors = (nodeGuid: string): TreeNodeDto[] => {
  const ancestors: TreeNodeDto[] = [];
  let current = flatNodeMap.value.get(nodeGuid);
  while (current?.parentTreeNodeGuid) {
    const parent = flatNodeMap.value.get(current.parentTreeNodeGuid);
    if (parent) {
      ancestors.unshift(parent);
      current = parent;
    } else {
      break;
    }
  }
  return ancestors;
};

// Handle node click (single select mode)
const handleNodeClickAndCollapse = (data: ElTreeNode) => {
  if (props.disabled) return;
  if (props.showCheckbox) return;

  const node = flatNodeMap.value.get(data.id);
  if (!node) return;

  emit("update:modelValue", [data.id]);
  emit("nodeSelected", { node, ancestors: getAncestors(data.id) });

  if (props.collapsible) editing.value = false;
};

// Handle checkbox changes
// When multiple is false (default), only one node can be checked at a time.
const handleCheckAndCollapse = (
  data: ElTreeNode,
  treeState: { checkedKeys: string[] },
) => {
  if (props.disabled) return;

  if (props.multiple) {
    emit("update:modelValue", [...treeState.checkedKeys]);
  } else {
    const tree = treeRef.value;
    if (tree) {
      const isNowChecked = treeState.checkedKeys.includes(data.id);
      tree.setCheckedKeys(isNowChecked ? [data.id] : []);
      emit("update:modelValue", isNowChecked ? [data.id] : []);
    }
    // Single-select: collapse after picking
    if (props.collapsible) editing.value = false;
  }

  const node = flatNodeMap.value.get(data.id);
  if (node) {
    emit("nodeSelected", { node, ancestors: getAncestors(data.id) });
  }
};

// Display the selected path as breadcrumb
const selectedPath = computed(() => {
  if (props.modelValue.length === 0) return "";

  // Find the deepest selected node
  let deepest: TreeNodeDto | undefined;
  let maxDepth = -1;

  for (const guid of props.modelValue) {
    const node = flatNodeMap.value.get(guid);
    if (node && node.depth > maxDepth) {
      maxDepth = node.depth;
      deepest = node;
    }
  }

  if (!deepest) return "";

  const ancestors = getAncestors(deepest.guid);
  return [...ancestors.map((a) => a.name), deepest.name].join(" > ");
});
</script>

<template>
  <div v-loading="loading" class="tree-selector">
    <!-- Collapsible: show path summary when not editing -->
    <div
      v-if="collapsible && selectedPath && !editing"
      class="tree-selector-path tree-selector-path--clickable"
      @click="editing = true"
    >
      {{ selectedPath }}
      <span class="tree-selector-edit-hint">click to change</span>
    </div>

    <!-- Non-collapsible: always show path if not in checkbox mode -->
    <div
      v-if="!collapsible && selectedPath && !showCheckbox"
      class="tree-selector-path"
    >
      {{ selectedPath }}
    </div>

    <!-- Show tree when: not collapsible, or collapsible and editing, or nothing selected yet -->
    <template v-if="!collapsible || editing || !selectedPath">
      <el-tree
        v-if="!loading && treeData.length > 0"
        ref="treeRef"
        :data="treeData"
        :props="treeProps"
        node-key="id"
        :default-expanded-keys="defaultExpandedKeys"
        :default-checked-keys="modelValue"
        :expand-on-click-node="false"
        :show-checkbox="showCheckbox"
        :indent="28"
        :highlight-current="!showCheckbox"
        @node-click="handleNodeClickAndCollapse"
        @check="handleCheckAndCollapse"
      >
        <template #default="{ data }">
          <span class="tree-selector-node">
            {{ data.label }}
            <span
              v-if="nodeCounts && nodeCounts.has(data.id)"
              class="number-available"
            >
              ({{ nodeCounts.get(data.id) }})
            </span>
            <el-tag
              v-if="showLevelName !== false"
              size="small"
              type="info"
              style="margin-left: 6px"
            >
              {{ data.levelName }}
            </el-tag>
          </span>
        </template>
      </el-tree>

      <el-button
        v-if="collapsible && editing"
        text
        size="small"
        style="margin-top: 4px"
        @click="editing = false"
      >
        Done
      </el-button>
    </template>

    <div
      v-if="!loading && treeData.length === 0"
      style="color: var(--el-text-color-secondary); padding: 12px"
    >
      {{ placeholder ?? "No tree data available" }}
    </div>
  </div>
</template>

<style scoped>
.el-tree {
  background: transparent;
}

.tree-selector {
  min-height: 50px;
}

.tree-selector-path {
  padding: 8px 12px;
  margin-bottom: 8px;
  background: var(--el-fill-color-light);
  border-radius: 4px;
  font-size: 13px;
  color: var(--el-text-color-regular);
}

.tree-selector-path--clickable {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.tree-selector-path--clickable:hover {
  background: var(--el-fill-color);
}

.tree-selector-edit-hint {
  font-size: 12px;
  color: var(--el-text-color-placeholder);
}

.tree-selector-node {
  display: flex;
  align-items: center;
}

.number-available {
  font-size: 0.85em;
  color: var(--el-color-primary-light-3);
  margin-left: 4px;
}
</style>
